// src/PasswordPage.tsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { AuthContext } from "./Auth/AuthProvider";
import { NativeAuthClient, NativeTokens } from "./Services/Auth/NativeAuthClient";
import AbpnSpinner from "./Shared/Components/AbpnSpinner";

const PasswordPage: React.FC = () => {
    const auth = React.useContext(AuthContext);
    const [params] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const initialEmail = params.get("email") || "";
    const [email, setEmail] = useState(initialEmail);
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const client = useMemo(() => new NativeAuthClient(), []);

    // 👇 NEW: detect first-time flag from query string OR navigation state
    const isFirstTime = useMemo(() => {
        const q = (params.get("firstTime") || "").toLowerCase();
        const fromState = (location.state as any)?.firstTime;
        return q === "1" || q === "true" || q === "yes" || fromState === true;
    }, [params, location.state]);

    const mountedRef = useRef(true);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    const doneRedirect = () => {
        const target = localStorage.getItem("redirectUri") || "/";
        try { localStorage.removeItem("redirectUri"); } catch { }
        navigate(target, { replace: true });
    };

    const onSubmit: React.FormEventHandler = async (e) => {
        e.preventDefault();
        if (!mountedRef.current) return;
        setError("");
        setBusy(true);

        const ctrl = new AbortController();
        abortRef.current = ctrl;

        try {
            const user = email.trim();
            if (!user) throw new Error("Please enter your email address.");

            // Normal Native Auth password flow
            const start = await client.signInInitiate(user, "password redirect");
            if (!mountedRef.current) return;

            await client.signInChallenge(start.continuation_token, "password redirect");
            if (!mountedRef.current) return;

            const tokens: NativeTokens = await client.signInTokenWithPassword(password.trim());
            if (!mountedRef.current) return;

            await auth.setNativeSession({ ...tokens, username: user });
            doneRedirect();
        } catch (err: any) {
            if (!mountedRef.current) return;

            // Handle expired/temporary password → send to SSPR
            if (err?.code === "PASSWORD_EXPIRED" || String(err?.message).includes("AADSTS50055")) {
                navigate(`/forgot?email=${encodeURIComponent(email)}&reason=expired&auto=1`, {
                    replace: true,
                    state: { from: location.pathname }
                });
                return;
            }

            console.error(err);
            setError(err?.message || "Sign-in failed.");
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    useEffect(() => {
        if (auth.isAuthenticated()) {
            doneRedirect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col xs={12} sm={10} md={7} lg={5}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title as="h2" className="h4 mb-3">
                                Enter your password
                            </Card.Title>

                            {/* 👇 NEW: First-time info banner */}
                            {isFirstTime && (
                                <Alert variant="info" className="mb-3">
                                    User has been created in Entra. You should receive an email with the temporary password.
                                </Alert>
                            )}

                            <Form onSubmit={onSubmit} noValidate>
                                <Form.Group className="mb-3" controlId="email">
                                    <Form.Label>Email address</Form.Label>
                                    <Form.Control
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={busy}
                                        placeholder="you@example.com"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-2" controlId="pwd">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={busy}
                                        placeholder="Enter your password"
                                    />
                                </Form.Group>

                                <div className="mb-3">
                                    <Link
                                        to={`/forgot?email=${encodeURIComponent(email)}`}
                                        state={{ from: location.pathname }}
                                    >
                                        Forgot password?
                                    </Link>
                                </div>

                                {error && (
                                    <Alert variant="danger" className="mb-3">
                                        {error}
                                    </Alert>
                                )}

                                <div className="d-grid">
                                    <Button type="submit" disabled={!email || !password || busy}>
                                        {busy ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                                Signing in…
                                            </>
                                        ) : (
                                            "Sign in"
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {busy && (
                        <div className="text-center mt-3">
                            <AbpnSpinner />
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default PasswordPage;
